import React, { useState } from 'react';
import { Play } from 'lucide-react';

/**
 * Parse a YouTube video id out of every form a director might paste:
 * watch?v=ID (with extra params), youtu.be/ID, /embed/ID, /shorts/ID, /live/ID
 */
export function parseYouTubeId(raw?: string | null): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const url = raw.trim();
  if (!url) return null;

  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  // Bare id
  if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
  return null;
}

interface EventPromoVideoProps {
  url?: string | null;
  title?: string;
  className?: string;
}

/**
 * Click-to-play YouTube promo video. The iframe is only mounted after the
 * visitor clicks the thumbnail. Unparseable URLs fall back to a plain link.
 */
const EventPromoVideo: React.FC<EventPromoVideoProps> = ({
  url,
  title = 'Event promo video',
  className = '',
}) => {
  const [playing, setPlaying] = useState(false);
  const trimmed = url?.trim();
  if (!trimmed) return null;

  const videoId = parseYouTubeId(trimmed);

  if (!videoId) {
    return (
      <a
        href={trimmed}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-3 hover:opacity-90 transition-opacity ${className}`}
      >
        <Play className="h-4 w-4" />
        Watch video
      </a>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const embedUrl =
    `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1` +
    (origin ? `&origin=${encodeURIComponent(origin)}` : '');

  return (
    <div className={`relative w-full overflow-hidden rounded-lg bg-black aspect-video ${className}`}>
      {playing ? (
        <iframe
          src={embedUrl}
          title={title}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${title}`}
          className="group absolute inset-0 h-full w-full"
        >
          <img
            src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
            alt={title}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />
          <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/40">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 shadow-lg">
              <Play className="h-7 w-7 translate-x-[2px] fill-white text-white" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

export default EventPromoVideo;
